import { useState, useCallback, useEffect, useMemo } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import { MapWrapper } from "./components/MapWrapper";
import { SearchComponent } from "./components/SearchComponent";
import LoadingIndicator from "./components/LoadingIndicator";
import InternationalMetaTagsManager from "./components/InternationalMetaTagsManager";
import Navigation from "./components/Navigation";
import RestoApiService from "./utils/RestoApiService";
import { MapLocation } from "./utils/MarkerClusterManager";
import { sidebarStateManager, SidebarType } from "./utils/SidebarStateManager";
import { useSidebarState } from "./hooks/useSidebarState";
import BaseSidebar from "./components/BaseSidebar";
import RecentReportsContent from "./components/RecentReportsContent";
import RestaurantDetailsSidebar from "./components/RestaurantDetailsSidebar";
import TrendsContent from "./components/TrendsContent";
import ChainContent from "./components/ChainContent";
import ChainRankingsContent from "./components/ChainRankingsContent";
import BacklogContent from "./components/BacklogContent";
import { SEOHead } from "./components/SEOHead";
import { HreflangManager } from "./components/HreflangManager";
import AnalyticsManager from "./utils/AnalyticsManager";
import NavigationManager from "./utils/NavigationManager";
import { recordRecentlyViewed } from "./hooks/useRecentlyViewed";
import { extractRestoId, generateRestoSlug } from "./utils/RestoSlugUtils";
import { ClientOnly } from "./components/ClientOnly";
import { TurnstileGate } from "./components/TurnstileGate";
import CityStatsContent from "./components/CityStatsContent";
import ProvinceStatsContent from "./components/ProvinceStatsContent";

import "./App.css";

// Define the interface expected from SearchComponent
interface SelectedRestaurantInfo {
  id: string | number;
  location: google.maps.LatLngLiteral; // Use google.maps namespace
}

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

interface AppProps {
  initialRestaurant?: MapLocation['entry'] | null;
  initialReports?: any[];
  initialLatestReports?: any[];
  initialTrends?: { upward?: any[]; downward?: any[] };
  initialSidebar?: 'recent-reports' | 'trending' | 'city-stats' | 'province-stats' | 'chain-details' | 'chain-rankings' | 'backlog' | 'restaurant-details';
  citySlug?: string;
  provinceSlug?: string;
  chainSlug?: string;
  initialCityStats?: any;
  initialProvinceStats?: any;
  initialChainStats?: any;
  initialChainRankings?: any;
  initialChain?: any;
  initialBacklog?: any;
  restoSlug?: string | null;
}

function App({ initialRestaurant, initialReports, initialLatestReports, initialTrends, initialSidebar, citySlug, provinceSlug, chainSlug, initialCityStats, initialProvinceStats, initialChainStats, initialChainRankings, initialChain, initialBacklog, restoSlug }: AppProps) {
  const [selectedLocation, setSelectedLocation] = useState<google.maps.LatLngLiteral | null>(null); // Use google.maps namespace
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | number | null>(initialRestaurant?.id ?? null);
  const [selectedEntry, setSelectedEntry] = useState<MapLocation['entry'] | null>(initialRestaurant ?? null);
  const [apiErrorCount, setApiErrorCount] = useState<Record<string, number>>({});
  const MAX_API_RETRIES = 2; // Maximum number of retries for a failed API call
  
  // UI State for improvements
  const [showHeaderNavigation] = useState(false);
  
  // Sidebar state management
  const sidebarState = useSidebarState(sidebarStateManager);

  // Auto-open sidebar based on route. SSR opens the same sidebar synchronously
  // in entry-server.tsx; this effect re-syncs after client hydration.
  useEffect(() => {
    if (initialSidebar === 'recent-reports') {
      sidebarState.openSidebar(SidebarType.RECENT_REPORTS);
    } else if (initialSidebar === 'trending') {
      sidebarState.openSidebar(SidebarType.TRENDS);
    } else if (initialSidebar === 'city-stats') {
      sidebarState.openSidebar(SidebarType.CITY_STATS);
    } else if (initialSidebar === 'province-stats') {
      sidebarState.openSidebar(SidebarType.PROVINCE_STATS);
    } else if (initialSidebar === 'chain-details') {
      sidebarState.openSidebar(SidebarType.CHAIN_DETAILS);
    } else if (initialSidebar === 'chain-rankings') {
      sidebarState.openSidebar(SidebarType.CHAIN_RANKINGS);
    } else if (initialSidebar === 'backlog') {
      sidebarState.openSidebar(SidebarType.BACKLOG);
    } else if (initialSidebar === 'restaurant-details') {
      sidebarState.openSidebar(SidebarType.RESTAURANT_DETAILS);
    }
  }, [initialSidebar]);

  // Initialize analytics and navigation manager on mount and manage body scroll
  useEffect(() => {
    // Initialize NavigationManager early
    NavigationManager.getInstance();
    
    // Listen for restaurant selections from trends
    const handleRestaurantSelectedEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const restaurantId = customEvent.detail.restaurantId;
      if (restaurantId) {
        setSelectedRestaurantId(restaurantId);
      }
    };
    
    window.addEventListener('restaurantSelected', handleRestaurantSelectedEvent);
    
    // Dynamic viewport height calculation for mobile browsers
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    // Set initial viewport height
    setViewportHeight();
    
    // Update on resize and orientation change
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);
    
    // Android device detection for navigation bar handling
    if (/Android/.test(navigator.userAgent)) {
      document.body.classList.add('android-device');
    }
    
    // Initial page view is sent by entry-client.tsx; SPA navigations are
    // tracked by Router's popstate handler.

    // Add no-scroll class to prevent scrolling on map view
    document.documentElement.classList.add('no-scroll');
    document.body.classList.add('no-scroll');
    
    // Cleanup: remove event listeners and no-scroll class when component unmounts
    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
      window.removeEventListener('restaurantSelected', handleRestaurantSelectedEvent);
      document.documentElement.classList.remove('no-scroll');
      document.body.classList.remove('no-scroll');
    };
  }, []);
  
  // Effect to check for restoId in URL on initial load only
  useEffect(() => {
    // First check path-based restaurant URL: /{lang}/restaurant/{slug}
    if (restoSlug) {
      const numericId = extractRestoId(restoSlug);
      setSelectedRestaurantId(numericId);
      return;
    }

    // Fallback: check query param (backward compat)
    const params = new URLSearchParams(window.location.search);
    const restoIdFromUrl = params.get("restoId");

    if (restoIdFromUrl) {
      const numericId = extractRestoId(restoIdFromUrl);
      setSelectedRestaurantId(numericId);
    }
  }, []); // Run only once on mount

  // Separate effect for navigation handlers
  useEffect(() => {
    // Handle browser back/forward navigation
    const handlePopState = () => {
      // Check path-based restaurant URL first
      const pathMatch = window.location.pathname.match(/\/restaurant\/(.+)$/);
      if (pathMatch) {
        const newRestoId = extractRestoId(pathMatch[1]);
        setSelectedRestaurantId(newRestoId);
        return;
      }

      // Fallback: check legacy query param
      const newParams = new URLSearchParams(window.location.search);
      const newRestoIdSlug = newParams.get("restoId");
      const newRestoId = newRestoIdSlug ? extractRestoId(newRestoIdSlug) : null;
      setSelectedRestaurantId(newRestoId);
    };
    
    // Handle custom event from RecentReportItem
    const handleRestaurantSelected = (event: CustomEvent) => {
      const { restaurantId, openSidebar } = event.detail;
      setSelectedRestaurantId(restaurantId);
      
      // Open restaurant details sidebar if requested
      if (openSidebar) {
        sidebarState.openSidebar(SidebarType.RESTAURANT_DETAILS);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('restaurantSelected', handleRestaurantSelected as EventListener);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('restaurantSelected', handleRestaurantSelected as EventListener);
    };
  }, [sidebarState]);
  
  // Effect to update URL when selectedRestaurantId or selectedEntry changes
  useEffect(() => {
    // Get current language from URL path
    const langMatch = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
    const lang = langMatch ? langMatch[1] : 'en';

    if (selectedRestaurantId) {
      // Check if the URL already has this restaurant in path-based format
      const pathMatch = window.location.pathname.match(/\/restaurant\/(.+)$/);
      const currentPathSlug = pathMatch ? pathMatch[1] : null;
      const currentPathRestoId = currentPathSlug ? extractRestoId(currentPathSlug) : null;

      // Also check legacy query param format
      const params = new URLSearchParams(window.location.search);
      const legacyRestoId = params.get("restoId");

      // Only use selectedEntry if it matches the current selectedRestaurantId
      const entryMatchesId = selectedEntry?.id && String(selectedEntry.id) === String(selectedRestaurantId);

      if (currentPathRestoId !== String(selectedRestaurantId)) {
        // Generate slug and navigate to path-based URL
        const slug = (selectedEntry?.name && entryMatchesId)
          ? generateRestoSlug(selectedEntry.name, selectedRestaurantId)
          : String(selectedRestaurantId);
        const url = `/${lang}/restaurant/${slug}`;
        history.pushState({ restoId: selectedRestaurantId }, "", url);
      } else if (selectedEntry?.name && entryMatchesId && currentPathSlug && /^\d+$/.test(currentPathSlug)) {
        // Path has numeric-only ID but we now have the name - upgrade to slug
        const slug = generateRestoSlug(selectedEntry.name, selectedRestaurantId);
        const url = `/${lang}/restaurant/${slug}`;
        history.replaceState({ restoId: selectedRestaurantId }, "", url);
      } else if (legacyRestoId) {
        // Migrate from legacy query param format to path-based
        const slug = (selectedEntry?.name && entryMatchesId)
          ? generateRestoSlug(selectedEntry.name, selectedRestaurantId)
          : String(selectedRestaurantId);
        const url = `/${lang}/restaurant/${slug}`;
        history.replaceState({ restoId: selectedRestaurantId }, "", url);
      }
    } else {
      // Navigate back to homepage when no restaurant selected
      const isOnRestaurantPage = window.location.pathname.includes('/restaurant/');
      const hasLegacyParam = new URLSearchParams(window.location.search).get("restoId");
      if (isOnRestaurantPage || hasLegacyParam) {
        history.replaceState({}, "", `/${lang}`);
      }
    }
  }, [selectedRestaurantId, selectedEntry]);
  // Always show map view since toggle buttons have been removed
  const [rangeValue, setRangeValue] = useState<number[]>([2]); // Default range value as array with default emoji index 2
  const [isFiltering, setIsFiltering] = useState(false);
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(null); // State for map bounds
  
  // Create a RestoApiService instance
  const restoApiService = useMemo(() => new RestoApiService(), []);

  const handleRestaurantSelect = useCallback((data: SelectedRestaurantInfo | null) => {
    setSelectedLocation(data ? data.location : null);
    setSelectedRestaurantId(data ? data.id : null);
    
    // Open restaurant details sidebar when selecting a restaurant
    if (data) {
      sidebarState.openSidebar(SidebarType.RESTAURANT_DETAILS);
    } else {
      sidebarState.closeSpecificSidebar(SidebarType.RESTAURANT_DETAILS);
    }
  }, [sidebarState]);

  // Handler for deselecting a restaurant and closing all sidebars
  const handleRestaurantDeselect = useCallback(() => {
    setSelectedLocation(null);
    setSelectedRestaurantId(null);
    // Close all sidebars when deselecting (global deselection concept)
    sidebarState.closeAllSidebars();
  }, [sidebarState]);


  // Handler for emoji range changes from SearchComponent
  const handleEmojiRangeChange = useCallback((emojiRange: number[]) => {
    // If no emojis selected, default to middle emoji (index 2)
    // This ensures consistency between EmojiSelector and MapWrapper
    setRangeValue(emojiRange);
    
    // Log for debugging
    
  }, []);
  
  // Handler for map bounds changes from MapWrapper
  const handleMapBoundsChange = useCallback((bounds: google.maps.LatLngBounds | null) => {
    
    setMapBounds(bounds);
  }, []);
  
  // Add effect to monitor mapBounds changes
  useEffect(() => {
    
  }, [mapBounds]);

  // Effect to fetch restaurant data when selectedRestaurantId changes
  useEffect(() => {
    // Track the current request to prevent race conditions
    let isCancelled = false;
    
    if (selectedRestaurantId) {
      const restoId = selectedRestaurantId.toString();
      
      // Check if we've already tried this ID too many times
      const errorCount = apiErrorCount[restoId] || 0;
      if (errorCount >= MAX_API_RETRIES) {
        // Clear the restaurant ID to break the loop
        setSelectedRestaurantId(null);
        return;
      }
      
      // Fetch restaurant data and update selectedEntry
      restoApiService.fetchRestoById(restoId)
        .then(result => {
          if (isCancelled) return;
          
          if (result.success && result.data) {
            setSelectedEntry(result.data);

            // Track restaurant view
            const analytics = AnalyticsManager.getInstance();
            analytics.trackRestaurantView(
              Number(result.data.id),
              result.data.name || 'Unknown Restaurant'
            );

            // Remember for the "recently viewed" list in search (needs
            // coordinates so selecting it later can pan the map)
            if (typeof result.data.latitude === 'number' && typeof result.data.longitude === 'number') {
              recordRecentlyViewed({
                id: result.data.id,
                name: result.data.name,
                address: result.data.address || undefined,
                latitude: result.data.latitude,
                longitude: result.data.longitude,
                inspectionForks: Number(result.data.inspectionForks) || undefined,
              });
            }
            
            // Reset error count on success
            if (apiErrorCount[restoId]) {
              setApiErrorCount(prev => {
                const newCounts = {...prev};
                delete newCounts[restoId];
                return newCounts;
              });
            }
          } else {
            // Increment error count for this ID
            setApiErrorCount(prev => ({
              ...prev,
              [restoId]: (prev[restoId] || 0) + 1
            }));
            
            // If we've reached max retries, clear the restaurant ID to break the loop
            if ((apiErrorCount[restoId] || 0) + 1 >= MAX_API_RETRIES) {
              setSelectedRestaurantId(null);
            }
          }
        })
        .catch(_ => {
          if (isCancelled) return;
          
          // Increment error count for this ID
          setApiErrorCount(prev => ({
            ...prev,
            [restoId]: (prev[restoId] || 0) + 1
          }));
          
          // If we've reached max retries, clear the restaurant ID to break the loop
          if ((apiErrorCount[restoId] || 0) + 1 >= MAX_API_RETRIES) {
            setSelectedRestaurantId(null);
          }
        });
    } else {
      setSelectedEntry(null);
    }
    
    // Cleanup function to cancel the request if component unmounts or ID changes
    return () => {
      isCancelled = true;
    };
  }, [selectedRestaurantId, apiErrorCount, restoApiService]);

  // Wall of Shame toggle functionality removed along with navigation buttons

  const mapPlaceholder = (
    <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
      <div className="text-gray-500 dark:text-gray-400 text-center">
        <div className="animate-pulse mb-2">
          <div className="w-16 h-16 mx-auto bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>
        <p>Loading map...</p>
      </div>
    </div>
  );

  return (
    <>
      <SEOHead page="home" restaurant={selectedEntry} />
      <HreflangManager />
      <InternationalMetaTagsManager selectedEntry={selectedEntry} pageType="homepage" />
      <div className="flex flex-col h-screen">
        {/* Conditionally render Navigation Header */}
        {showHeaderNavigation && <Navigation variant="header" />}

        <div className="flex-grow w-full relative overflow-hidden">
          <div className="relative h-full">
            <ClientOnly fallback={mapPlaceholder}>
              {/* Hoisted out of the gate: neither touches the Maps runtime, so search
                  stays usable while the challenge runs. mapBounds is null until the
                  map mounts, which only drops autocomplete viewport bias. */}
              <div className="absolute top-0 left-0 z-10 w-full max-w-md pl-2">
                <SearchComponent
                  onRestaurantSelect={handleRestaurantSelect}
                  selectedRestaurantId={selectedRestaurantId}
                  mapBounds={mapBounds}
                  onEmojiRangeChange={handleEmojiRangeChange}
                />
              </div>
              <LoadingIndicator isLoading={isFiltering} />

              <TurnstileGate placeholder={mapPlaceholder}>
                <APIProvider apiKey={API_KEY} libraries={["marker", "geometry"]}>
                  <MapWrapper
                    selectedLocation={selectedLocation}
                    rangeValue={rangeValue}
                    setIsFiltering={setIsFiltering}
                    selectedRestaurantId={selectedRestaurantId}
                    onRestaurantDeselect={handleRestaurantDeselect}
                    onRestaurantSelect={handleRestaurantSelect}
                    onMapBoundsChange={handleMapBoundsChange}
                  />
                </APIProvider>
              </TurnstileGate>
            </ClientOnly>

            {/* Restaurant Details Sidebar */}
            <RestaurantDetailsSidebar
              selectedEntry={selectedEntry}
              selectedRestaurantId={selectedRestaurantId}
              initialReports={initialReports}
              initialChain={initialChain}
            />

            {/* Recent Reports Sidebar */}
            <BaseSidebar
              sidebarType={SidebarType.RECENT_REPORTS}
              title="Recent Reports"
            >
              <RecentReportsContent initialReports={initialLatestReports} />
            </BaseSidebar>

            {/* Trends Sidebar */}
            <BaseSidebar
              sidebarType={SidebarType.TRENDS}
              title="Trends"
            >
              <TrendsContent
                citySlug={citySlug}
                initialUpward={initialTrends?.upward}
                initialDownward={initialTrends?.downward}
              />
            </BaseSidebar>

            {/* City Stats Sidebar */}
            <BaseSidebar sidebarType={SidebarType.CITY_STATS}>
              <CityStatsContent citySlug={citySlug} initialData={initialCityStats} />
            </BaseSidebar>

            {/* Province Stats Sidebar */}
            <BaseSidebar sidebarType={SidebarType.PROVINCE_STATS}>
              <ProvinceStatsContent provinceSlug={provinceSlug} initialData={initialProvinceStats} />
            </BaseSidebar>

            {/* Chain Detail Sidebar */}
            <BaseSidebar sidebarType={SidebarType.CHAIN_DETAILS}>
              <ChainContent chainSlug={chainSlug} initialData={initialChainStats} />
            </BaseSidebar>

            {/* Chain Rankings Sidebar */}
            <BaseSidebar sidebarType={SidebarType.CHAIN_RANKINGS}>
              <ChainRankingsContent initialData={initialChainRankings} />
            </BaseSidebar>

            {/* Inspection Backlog Sidebar */}
            <BaseSidebar sidebarType={SidebarType.BACKLOG}>
              <BacklogContent initialData={initialBacklog} />
            </BaseSidebar>

          </div>
        </div>

        {/* Footer Navigation */}
        <Navigation variant="footer" />
      </div>
    </>
  );
}

export default App;
